import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Pause, Play } from "lucide-react";
import { parseYouTubeId } from "@/lib/api";

/**
 * HeroVideoSlider — full-width home hero that plays videos one after another.
 *
 *  - Each slide is either a YouTube video (source: "youtube") or a file stored
 *    on our own server (source: "upload").
 *  - When a video ends, the next one starts automatically (loops back to the first).
 *  - Visitors can jump to any video with the arrows, the numbered tabs, or by swiping.
 *  - Autoplay requires muted playback in all browsers; a sound toggle is provided.
 */

// ---- YouTube IFrame API loader (loaded once, shared) ----
let ytPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytPromise) return ytPromise;
  ytPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.body.appendChild(tag);
  });
  return ytPromise;
};

// ---- YouTube slide ----
const YouTubeSlide = ({ videoId, muted, paused, onEnded, onProgress, single }) => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const cbRef = useRef({ onEnded, onProgress, single });
  cbRef.current = { onEnded, onProgress, single };

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let ready = false;
    // If YouTube can't load (ad-blocker, blocked network), move on instead of stalling.
    const watchdog = setTimeout(() => {
      if (!ready && !cancelled && !cbRef.current.single) cbRef.current.onEnded();
    }, 10000);
    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      // YouTube swaps this node for an <iframe>, so create it outside React's tree.
      const mount = document.createElement("div");
      mount.style.width = "100%";
      mount.style.height = "100%";
      hostRef.current.innerHTML = "";
      hostRef.current.appendChild(mount);
      playerRef.current = new YT.Player(mount, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          playsinline: 1,
          modestbranding: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            ready = true;
            clearTimeout(watchdog);
            if (muted) e.target.mute(); else e.target.unMute();
            e.target.playVideo();
            timer = setInterval(() => {
              const p = playerRef.current;
              if (!p || typeof p.getDuration !== "function") return;
              const d = p.getDuration();
              if (d > 0) cbRef.current.onProgress(p.getCurrentTime() / d);
            }, 250);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) {
              if (cbRef.current.single) {
                e.target.seekTo(0);
                e.target.playVideo();
              } else {
                cbRef.current.onEnded();
              }
            }
          },
          onError: () => {
            // Unavailable / embedding disabled — skip to the next video.
            setTimeout(() => !cancelled && cbRef.current.onEnded(), 2500);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      if (timer) clearInterval(timer);
      try { playerRef.current && playerRef.current.destroy(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, [videoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = playerRef.current;
    if (!p || typeof p.mute !== "function") return;
    if (muted) p.mute(); else p.unMute();
  }, [muted]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p || typeof p.pauseVideo !== "function") return;
    if (paused) p.pauseVideo(); else p.playVideo();
  }, [paused]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {/* Oversized 16:9 frame so the video always covers the hero (like object-fit: cover) */}
      <div ref={hostRef} className="hero-yt-cover" />
    </div>
  );
};

// ---- Uploaded (self-hosted) video slide ----
const FileSlide = ({ src, poster, muted, paused, onEnded, onProgress, single }) => {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = muted;
  }, [muted]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => { /* autoplay blocked — stays on poster */ });
  }, [paused, src]);

  return (
    <video
      ref={ref}
      key={src}
      src={src}
      poster={poster || undefined}
      autoPlay
      muted={muted}
      playsInline
      loop={single}
      preload="auto"
      onEnded={onEnded}
      onError={() => setTimeout(onEnded, 2500)}
      onTimeUpdate={(e) => {
        const v = e.currentTarget;
        if (v.duration > 0) onProgress(v.currentTime / v.duration);
      }}
      className="absolute inset-0 w-full h-full object-cover bg-black"
    />
  );
};

export default function HeroVideoSlider({ videos = [], kicker }) {
  const list = videos.filter(
    (v) =>
      v.active !== false &&
      ((v.source === "upload" && v.video_url) ||
        (v.source !== "upload" && (v.youtube_id || parseYouTubeId(v.youtube_url))))
  );
  const total = list.length;

  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const touchX = useRef(null);

  useEffect(() => {
    if (idx >= total && total > 0) setIdx(0);
  }, [total, idx]);

  const go = useCallback(
    (n) => {
      if (!total) return;
      setProgress(0);
      setPaused(false);
      setIdx(((n % total) + total) % total);
    },
    [total]
  );
  const next = useCallback(() => {
    setProgress(0);
    setIdx((i) => (total ? (i + 1) % total : 0));
  }, [total]);
  const prev = () => go(idx - 1);

  // Keyboard arrows when the hero is in view
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (window.scrollY > window.innerHeight * 0.8) return;
      if (e.key === "ArrowRight") go(idx + 1);
      if (e.key === "ArrowLeft") go(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, idx]);

  if (!total) return null;
  const cur = list[Math.min(idx, total - 1)];
  const single = total === 1;
  const ytId = cur.source === "upload" ? "" : cur.youtube_id || parseYouTubeId(cur.youtube_url);

  return (
    <section
      className="relative w-full h-[calc(100svh-5rem)] min-h-[420px] overflow-hidden bg-ahm-teal select-none"
      data-testid="hero-video-slider"
      data-active-index={idx}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) (dx < 0 ? go(idx + 1) : go(idx - 1));
        touchX.current = null;
      }}
    >
      <AnimatePresence mode="sync" initial={false}>
        <motion.div
          key={cur.id || idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          {cur.source === "upload" ? (
            <FileSlide
              src={cur.video_url}
              poster={cur.poster}
              muted={muted}
              paused={paused}
              single={single}
              onEnded={next}
              onProgress={setProgress}
            />
          ) : (
            <YouTubeSlide
              videoId={ytId}
              muted={muted}
              paused={paused}
              single={single}
              onEnded={next}
              onProgress={setProgress}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Shade + click-shield (stops YouTube UI from hijacking clicks/swipes) */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/70 via-black/10 to-black/25 pointer-events-auto" />

      {/* Caption */}
      <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-28 md:pb-32">
          {kicker && (
            <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-ahm-yellow font-semibold">{kicker}</p>
          )}
          <AnimatePresence mode="wait">
            <motion.h2
              key={`t-${cur.id || idx}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.6 }}
              className="font-display text-4xl sm:text-5xl md:text-7xl text-ahm-cream leading-[0.95] mt-3 max-w-4xl text-balance"
            >
              {cur.title}
            </motion.h2>
          </AnimatePresence>
        </div>
      </div>

      {/* Arrows */}
      {!single && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous video"
            data-testid="hero-video-prev"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-14 md:h-14 rounded-full bg-black/30 backdrop-blur border border-white/30 text-white hover:bg-ahm-orange hover:border-ahm-orange transition flex items-center justify-center"
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            onClick={() => go(idx + 1)}
            aria-label="Next video"
            data-testid="hero-video-next"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-14 md:h-14 rounded-full bg-black/30 backdrop-blur border border-white/30 text-white hover:bg-ahm-orange hover:border-ahm-orange transition flex items-center justify-center"
          >
            <ChevronRight />
          </button>
        </>
      )}

      {/* Bottom bar: slide tabs with progress + controls */}
      <div className="absolute inset-x-0 bottom-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-6 md:pb-8 flex items-end gap-4">
          <div className="flex-1 grid gap-2 md:gap-3" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
            {list.map((v, i) => (
              <button
                key={v.id || i}
                type="button"
                onClick={() => go(i)}
                data-testid={`hero-video-dot-${i}`}
                aria-label={`Play video ${i + 1}: ${v.title || ""}`}
                className="group text-left"
              >
                <span className="block h-[3px] w-full bg-white/30 overflow-hidden rounded-full">
                  <span
                    className="block h-full bg-ahm-orange"
                    style={{
                      width: i < idx ? "100%" : i === idx ? `${Math.round(progress * 100)}%` : "0%",
                      transition: i === idx ? "width 250ms linear" : "none",
                    }}
                  />
                </span>
                <span
                  className={`hidden md:block mt-2 text-[11px] uppercase tracking-[0.2em] truncate transition ${
                    i === idx ? "text-white" : "text-white/55 group-hover:text-white"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} · {v.title}
                </span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Play" : "Pause"}
              data-testid="hero-video-pause"
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-ahm-teal transition flex items-center justify-center"
            >
              {paused ? <Play size={16} /> : <Pause size={16} />}
            </button>
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="hero-video-mute"
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-ahm-teal transition flex items-center justify-center"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
